import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'
import { supabase } from '@/lib/supabase/client'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { formatDateDDMMYYYY, getLogoDataUrl } from './ndtPdfShared'
import { drawMethodTitle } from './pdfTitle'
import type { Job, NdtSubtype } from '@/services/jobs.service'
import { getNextMethodReportSequence, type NDTReportMethod } from '@/utils/getNextReportSequence'
import { DEFAULT_MT_EQUIPMENT } from '@/types/mtTypes'
import { DEFAULT_VT_EQUIPMENT } from '@/types/vtTypes'
import { DEFAULT_PT_EQUIPMENT } from '@/types/ptTypes'
import { DEFAULT_LT_EQUIPMENT, DEFAULT_LT_TEST_PROCEDURE } from '@/types/ltTypes'
import { DEFAULT_ET_PROBE_SETTINGS } from '@/types/etTypes'
import { createDefaultCTHFormData, mapCTHFormToPDFData } from '@/types/cthThreadTypes'
import { generateMTReport } from './mtPDFTemplate'
import { generateVTReport } from './vtPDFTemplate'
import { generatePTReport } from './ptPDFTemplate'
import { generateLTReport } from './ltPDFTemplate'
import { generateUTReport } from './utPDFTemplate'
import { generateRTReport } from './rtPDFTemplate'
import { generateETReport } from './etPDFTemplate'
import { generateCTHReport } from './cthThreadPDFTemplate'

const REPORT_METHOD: Record<NdtSubtype, NDTReportMethod> = {
  ET: 'ET',
  MPI: 'MT',
  PT: 'PT',
  VT: 'VT',
  LT: 'LT',
  UT: 'UT',
  RT: 'RT',
  Thread: 'CTH',
}

const DEFAULT_TEST_SITE = 'Bergen'

/** Templates append their own `%`, so `"100%"` is passed on as `"100"`. */
const extentValue = (extent: string | undefined) => {
  const trimmed = (extent || '').trim()
  if (!trimmed) return '100'
  const numeric = trimmed.replace(/\s*%$/, '')
  return Number.isNaN(Number(numeric)) ? trimmed : numeric
}

interface JobReportContext {
  testDate: string
  testSite: string
  reportNumber: string
  issuedBy: string
  /** Whoever created the job; verifies the merged report. */
  createdBy: string
  objectDescription: string
  objects: string[]
  companyName: string
}

function buildContext(job: Job): JobReportContext {
  const objects = job.assignments
    .map((a) => a.equipment)
    .filter((equipment) => equipment !== null)
    .map((equipment) => [equipment.serial_number || equipment.owner_id, equipment.name].filter(Boolean).join(' - '))
    .filter(Boolean)
  const objectDescription = job.description || objects[0] || ''
  return {
    testDate: new Date().toISOString().split('T')[0],
    testSite: job.location?.trim() || DEFAULT_TEST_SITE,
    reportNumber: job.order_number?.trim() || '',
    issuedBy: job.inspector_name || '',
    createdBy: job.creator?.full_name || '',
    objectDescription,
    objects: objects.length > 0 ? objects : [objectDescription],
    companyName: job.company_name || '',
  }
}

async function generateMethodReport(method: NdtSubtype, extent: string, ctx: JobReportContext): Promise<Blob> {
  const { sequenceNumber, reportId } = await getNextMethodReportSequence(REPORT_METHOD[method], ctx.reportNumber)
  const common = {
    testDate: ctx.testDate,
    testSite: ctx.testSite,
    reportNumber: ctx.reportNumber,
    objects: ctx.objects,
    objectDescription: ctx.objectDescription,
    issuedBy: ctx.issuedBy,
    status: 'Accepted' as const,
    certificationNumber: '---',
    sequenceNumber,
    extentOfExamination: extent,
    company: ctx.companyName,
  }

  switch (method) {
    case 'MPI':
      return generateMTReport({
        ...common,
        surfaceCondition: 'Cleaned',
        equipmentSettings: { ...DEFAULT_MT_EQUIPMENT },
        material: 'Carbon steel',
        mpiType: 'mpi',
      })
    case 'VT':
      return generateVTReport({
        ...common,
        objectType: 'Weld',
        equipmentSettings: { ...DEFAULT_VT_EQUIPMENT },
        material: 'Carbon steel',
      })
    case 'PT':
      return generatePTReport({
        ...common,
        objectType: 'Weld',
        equipmentSettings: { ...DEFAULT_PT_EQUIPMENT },
        material: 'Stainless steel',
      })
    case 'LT':
      return generateLTReport({
        ...common,
        objectType: 'Component',
        equipmentSettings: { ...DEFAULT_LT_EQUIPMENT },
        material: 'Carbon steel',
        testProcedure: DEFAULT_LT_TEST_PROCEDURE,
      })
    case 'UT':
      return generateUTReport({ ...common, material: 'Carbon steel' })
    case 'RT':
      return generateRTReport({ ...common, material: 'Carbon steel' })
    case 'ET':
      return generateETReport({
        ...common,
        unitId: ctx.objectDescription,
        isChainSling: false,
        objectType: 'custom',
        customObjectType: ctx.objectDescription,
        probeSettings: DEFAULT_ET_PROBE_SETTINGS,
        material: 'Carbon steel',
        attachedImages: [],
      })
    case 'Thread':
      return generateCTHReport(
        mapCTHFormToPDFData(
          {
            ...createDefaultCTHFormData(),
            testDate: ctx.testDate,
            testSite: ctx.testSite,
            reportNumber: ctx.reportNumber,
            objectDescription: ctx.objectDescription,
            issuedBy: ctx.issuedBy,
          },
          reportId,
          ctx.companyName,
        ),
      )
  }
}

const DEPARTMENTS = [
  ['Dept. Ågotnes', 'Bleivassvegen 62', '5363 Ågotnes'],
  ['Dept. Bergen', 'Nygårdsviken 1', '5165 Laksevåg'],
  ['Dept. Mongstad', 'Storemyra 239', '5954 Mongstad'],
]

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .filter((_, i, all) => i === 0 || i === all.length - 1)
    .join('')

async function generateFrontPage(ctx: JobReportContext, description: string | null, docNumber: string): Promise<Blob> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const left = 12
  const right = 12
  const testDate = formatDateDDMMYYYY(ctx.testDate)

  const headerMargin = 15
  drawMethodTitle(doc, 'TECHNICAL', headerMargin, headerMargin, { trailing: 'REPORT' })
  const logoDataUrl = await getLogoDataUrl()
  if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', pageWidth - headerMargin - 40, headerMargin - 5, 40, 12)
  doc.setTextColor(0, 0, 0)
  const grid = {
    theme: 'grid' as const,
    margin: { left, right },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      textColor: [0, 0, 0] as [number, number, number],
      lineColor: [0, 0, 0] as [number, number, number],
      lineWidth: 0.2,
      cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
    },
  }

  autoTable(doc, {
    ...grid,
    startY: 48,
    body: [
      ['CLIENT', ctx.companyName.toUpperCase() || '---'],
      ['CLIENT CONTACT', '---'],
      ['PROJECT DESCRIPTION', description || '---'],
      ['PROJECT TRACEABILITY', '---'],
      ['STMI ORDER NO / DOC NO', docNumber || '---'],
      ['PLACE OF TEST', ctx.testSite],
      ['DATE OF TEST', testDate],
    ],
    columnStyles: { 0: { cellWidth: 48 } },
    didParseCell: (cell) => {
      if (cell.row.index === 0 && cell.column.index === 1) cell.cell.styles.fontStyle = 'bold'
    },
  })

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.text('Revision history:', left, 95)
  doc.setLineWidth(0.2)
  doc.line(left, 95.6, left + doc.getTextWidth('Revision history:'), 95.6)

  const issuedBy = initials(ctx.issuedBy)
  autoTable(doc, {
    ...grid,
    startY: 99,
    head: [['Rev.:', 'SUBJECT OF CHANGE AND/OR PURPOSE OF ISSUE:', 'Date:', 'Originator:', 'Verified:', 'Approval:']],
    body: [
      ['00', 'Issued', testDate, issuedBy, initials(ctx.createdBy), ''],
      ['', '', '', '', '', ''],
      ['', '', '', '', '', ''],
      ['', '', '', '', '', ''],
    ],
    headStyles: { fillColor: [255, 255, 255], fontStyle: 'normal', fontSize: 5.5, cellPadding: 0.6 },
    bodyStyles: { fontSize: 9, minCellHeight: 5 },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 18, halign: 'center' },
      4: { cellWidth: 17.5, halign: 'center' },
      5: { cellWidth: 19.5, halign: 'center' },
    },
  })

  doc.setFontSize(10)
  doc.setTextColor(90, 90, 90)
  DEPARTMENTS.forEach((lines, i) => {
    lines.forEach((line, row) => doc.text(line, 27 + i * 53, 208 + row * 5.5))
  })

  return doc.output('blob')
}

const HEADING_COLOR: [number, number, number] = [54, 95, 145]

const METHOD_TITLES: Record<NdtSubtype, string> = {
  VT: 'VISUAL INSPECTION',
  MPI: 'MAGNETIC PARTICLE TESTING',
  PT: 'PENETRANT TESTING',
  ET: 'EDDY CURRENT TESTING',
  UT: 'ULTRASONIC TESTING',
  RT: 'RADIOGRAPHIC TESTING',
  LT: 'LOAD TESTING',
  Thread: 'THREAD INSPECTION',
}

/** Optional logo files; any that are missing are left blank on the content page. */
const CERTIFICATION_LOGOS = import.meta.glob('/src/assets/certifications/*.{png,jpg,jpeg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

interface LogoBox {
  file: string
  x: number
  y: number
  w: number
  h: number
}

const CERTIFICATIONS: { label: string; number: string; y: number; logos: LogoBox[] }[] = [
  {
    label: 'Enterprise of Competence / Sertifisering Sakkyndig Virksomhet',
    number: '30337',
    y: 105,
    logos: [{ file: 'tff', x: 161, y: 85, w: 23, h: 22 }],
  },
  {
    label: 'NTO registered NDT organization',
    number: 'N-274',
    y: 118,
    logos: [
      { file: 'force', x: 155, y: 112, w: 16, h: 6.5 },
      { file: 'nordtest', x: 172, y: 112, w: 23.5, h: 6.5 },
    ],
  },
  {
    label: 'Non-Destructive Testing (NDT) on Classification Projects',
    number: 'AOSS0000D3Y',
    y: 128.5,
    logos: [{ file: 'dnv', x: 152.5, y: 124.5, w: 41, h: 17.5 }],
  },
  { label: 'Thickness Measurements on ships', number: 'AOSS0000ENF', y: 137, logos: [] },
  {
    label: 'ISO 9001 & ISO 14001',
    number: '676',
    y: 146.5,
    logos: [{ file: 'kiwa-accreditation', x: 158, y: 143, w: 34, h: 21 }],
  },
  {
    label: 'Industrial Radiography',
    number: 'GA-12 152',
    y: 167,
    logos: [{ file: 'dsa', x: 153, y: 164, w: 40, h: 8.5 }],
  },
]

const logoUrl = (file: string) =>
  Object.entries(CERTIFICATION_LOGOS).find(([path]) => path.split('/').pop()?.split('.')[0] === file)?.[1]

function loadImage(url: string): Promise<{ dataUrl: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      canvas.getContext('2d')?.drawImage(img, 0, 0)
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => resolve(null)
    img.src = url
  })
}

function drawHeading(doc: jsPDF, text: string, x: number, y: number, align: 'left' | 'center' = 'left') {
  doc.setFont('times', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(...HEADING_COLOR)
  doc.text(text, x, y, { align })
  doc.setTextColor(0, 0, 0)
}

async function generateContentPage(entries: { title: string; page: number }[]): Promise<Blob> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const left = 12
  const right = 197

  drawHeading(doc, 'Content', left, 25)
  doc.setFont('times', 'bold')
  doc.setFontSize(10)
  entries.forEach(({ title, page }, i) => {
    const y = 35 + i * 7
    const pageText = String(page)
    const titleEnd = left + doc.getTextWidth(title)
    const pageStart = right - doc.getTextWidth(pageText)
    doc.text(title, left, y)
    doc.text(pageText, right, y, { align: 'right' })
    const dotWidth = doc.getTextWidth('.')
    const dots = Math.max(0, Math.floor((pageStart - titleEnd - 1) / dotWidth))
    doc.setFont('times', 'normal')
    doc.text('.'.repeat(dots), titleEnd + 0.5, y)
    doc.setFont('times', 'bold')
  })

  const columns = { label: 13.5, number: 114, logo: 160 }
  doc.setFont('helvetica', 'bolditalic')
  doc.setFontSize(11)
  doc.setTextColor(60, 60, 60)
  doc.text('Approval / Certification', columns.label, 83)
  doc.text('Number', columns.number, 83)
  doc.text('By Authority', columns.logo, 83)

  doc.setFont('helvetica', 'italic')
  doc.setFontSize(7.5)
  for (const cert of CERTIFICATIONS) {
    doc.text(cert.label, columns.label, cert.y)
    doc.text(cert.number, columns.number, cert.y - 1)
  }
  doc.setTextColor(0, 0, 0)

  const logos = CERTIFICATIONS.flatMap((cert) => cert.logos)
  const images = await Promise.all(logos.map((logo) => {
    const url = logoUrl(logo.file)
    return url ? loadImage(url) : Promise.resolve(null)
  }))
  logos.forEach((box, i) => {
    const image = images[i]
    if (!image) return
    const scale = Math.min(box.w / image.width, box.h / image.height)
    const w = image.width * scale
    const h = image.height * scale
    doc.addImage(image.dataUrl, 'PNG', box.x + (box.w - w) / 2, box.y + (box.h - h) / 2, w, h)
  })

  return doc.output('blob')
}

const TESTED_ITEM_COLUMNS: { key: NdtSubtype | 'PMI'; label: string }[] = [
  { key: 'VT', label: 'VT' },
  { key: 'PT', label: 'PT' },
  { key: 'MPI', label: 'MT' },
  { key: 'ET', label: 'ET' },
  { key: 'RT', label: 'RT' },
  { key: 'PMI', label: 'PMI' },
  { key: 'UT', label: 'UT' },
]

function generateTestedItemsPage(job: Job, ctx: JobReportContext): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const left = 12
  const lineHeight = 3.5
  const introGap = 8
  const noteGap = 11

  const extraColumns = job.ndt_subtypes
    .filter((method) => !TESTED_ITEM_COLUMNS.some((col) => col.key === method))
    .map((method) => ({ key: method, label: method }))
  const columns = [...TESTED_ITEM_COLUMNS, ...extraColumns]
  const extentFor = (key: NdtSubtype | 'PMI') => {
    if (key === 'PMI' || !job.ndt_subtypes.includes(key)) return '---'
    return job.ndt_extents[key] || '100%'
  }
  const items = job.description ? [job.description] : ctx.objects
  const blankRow = Array<string>(columns.length + 3).fill('')

  const drawTable = (target: jsPDF, startY: number) => autoTable(target, {
    startY,
    theme: 'grid',
    margin: { left, right: left },
    head: [
      [
        { content: 'Part Description', styles: { halign: 'center' } },
        { content: 'Test Details', colSpan: columns.length + 2, styles: { halign: 'center' } },
      ],
      ['Details', ...columns.map((col) => col.label), 'Result', 'Comments'],
    ],
    body: [
      ...items.map((item) => [item, ...columns.map((col) => extentFor(col.key)), 'accepted', '---']),
      blankRow,
      blankRow,
      blankRow,
    ],
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
      halign: 'center',
      cellPadding: 0.6,
      minCellHeight: 4.5,
    },
    headStyles: { fillColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: extraColumns.length === 0
      ? {
          0: { cellWidth: 78 },
          1: { cellWidth: 11.8 },
          2: { cellWidth: 9.7 },
          3: { cellWidth: 11.8 },
          4: { cellWidth: 8.2 },
          5: { cellWidth: 9.7 },
          6: { cellWidth: 9.7 },
          7: { cellWidth: 8.2 },
          8: { cellWidth: 17 },
          9: { cellWidth: 21.9 },
        }
      : { 0: { cellWidth: 60 } },
  })
  const lastTableY = (target: jsPDF) =>
    (target as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY

  const scratch = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const scratchStart = 20
  drawTable(scratch, scratchStart)
  const tableHeight = lastTableY(scratch) - scratchStart

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  const subject = job.description || ctx.objects.join(', ')
  const introLines: string[] = doc.splitTextToSize(`STMI has performed NDT of ${subject}`, pageWidth - 2 * left)
  const introHeight = introLines.length * lineHeight
  const blockHeight = introHeight + introGap + tableHeight + noteGap
  const top = Math.max(30, (pageHeight - blockHeight) / 2)

  drawHeading(doc, 'TESTED ITEMS', pageWidth / 2, 17, 'center')
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.text(introLines, left, top + lineHeight, { lineHeightFactor: lineHeight / (8 * 0.3528) })
  drawTable(doc, top + introHeight + introGap)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.text('For test details: see the individual test reports.', left, lastTableY(doc) + noteGap)

  return doc.output('blob')
}

/**
 * One report per NDT method on the job, merged into a single PDF in the job's method order.
 * Jobs with more than one method get a front page, a content page and a tested items page.
 */
export async function generateNdtJobReport(job: Job, docNumber: string): Promise<Blob> {
  const ctx = buildContext(job)
  const methodReports = await Promise.all(
    job.ndt_subtypes.map(async (method) => {
      const blob = await generateMethodReport(method, extentValue(job.ndt_extents[method]), ctx)
      return { method, doc: await PDFDocument.load(await blob.arrayBuffer()) }
    }),
  )

  const toDoc = async (blob: Blob) => PDFDocument.load(await blob.arrayBuffer())
  const sources: PDFDocument[] = []
  if (methodReports.length > 1) {
    const [frontPage, testedItems] = await Promise.all([
      generateFrontPage(ctx, job.description, docNumber).then(toDoc),
      toDoc(generateTestedItemsPage(job, ctx)),
    ])
    const testedItemsPage = frontPage.getPageCount() + 2
    let nextPage = testedItemsPage + testedItems.getPageCount()
    const entries = [{ title: 'TESTED ITEMS', page: testedItemsPage }]
    for (const { method, doc } of methodReports) {
      entries.push({ title: METHOD_TITLES[method], page: nextPage })
      nextPage += doc.getPageCount()
    }
    sources.push(frontPage, await toDoc(await generateContentPage(entries)), testedItems)
  }
  sources.push(...methodReports.map((report) => report.doc))

  const merged = await PDFDocument.create()
  for (const source of sources) {
    const pages = await merged.copyPages(source, source.getPageIndices())
    pages.forEach((page) => merged.addPage(page))
  }
  if (sources.length > methodReports.length) await renumberPages(merged)
  const bytes = await merged.save()
  return new Blob([bytes as BlobPart], { type: 'application/pdf' })
}

const MM = 72 / 25.4

/**
 * Replaces each method report's own "Page i of n" footer (centered, 10 mm from the bottom)
 * with one numbering across the whole merged document.
 */
async function renumberPages(doc: PDFDocument) {
  const font = await doc.embedFont(StandardFonts.Helvetica)
  const fontSize = 8
  const baseline = 10 * MM
  const pages = doc.getPages()
  pages.forEach((page, i) => {
    const { width } = page.getSize()
    const text = `Page ${i + 1} of ${pages.length}`
    const textWidth = font.widthOfTextAtSize(text, fontSize)
    page.drawRectangle({
      x: width / 2 - 20 * MM,
      y: baseline - 1.5 * MM,
      width: 40 * MM,
      height: 5 * MM,
      color: rgb(1, 1, 1),
    })
    page.drawText(text, { x: (width - textWidth) / 2, y: baseline, size: fontSize, font, color: rgb(0, 0, 0) })
  })
}

export interface PreparedNdtJobReport {
  blob: Blob
  /** e.g. `TR-I2611000.001` */
  reportId: string
  storageFolder: string
}

async function nextTechnicalReportNumber(job: Job) {
  const orderNumber = job.order_number?.trim()
  if (!orderNumber) throw new Error('Jobben mangler ordrenummer')
  return getNextMethodReportSequence('TR', orderNumber)
}

/** Builds the job report numbered with the next free TR sequence for the job's order number. */
export async function prepareNdtJobReport(job: Job): Promise<PreparedNdtJobReport> {
  const { reportId, storageFolder } = await nextTechnicalReportNumber(job)
  return { blob: await generateNdtJobReport(job, reportId), reportId, storageFolder }
}

/**
 * Stores the report in the `reports` bucket and `ndt_reports` (linked to the job).
 * Regenerates it if someone published the previewed TR number in the meantime.
 */
export async function publishNdtJobReport(job: Job, preview: PreparedNdtJobReport): Promise<PreparedNdtJobReport> {
  const next = await nextTechnicalReportNumber(job)
  const report = next.reportId === preview.reportId
    ? preview
    : { blob: await generateNdtJobReport(job, next.reportId), ...next }
  const filePath = `${report.storageFolder}/${report.reportId}.pdf`

  const { error: uploadError } = await supabase.storage
    .from('reports')
    .upload(filePath, report.blob, { contentType: 'application/pdf' })
  if (uploadError) throw uploadError

  const { data: { user } } = await supabase.auth.getUser()
  const { error: insertError } = await supabase.from('ndt_reports').insert({
    order_number: report.reportId,
    unit_id: job.description || buildContext(job).objectDescription || null,
    test_date: new Date().toISOString().split('T')[0],
    test_type: 'TR',
    status: 'Accepted',
    issued_by: job.inspector_name || '',
    verified_by: job.creator?.full_name || null,
    file_path: filePath,
    company_id: job.company_id,
    job_id: job.id,
    created_by: user?.id,
    report_data: {
      ndt_subtypes: job.ndt_subtypes,
      ndt_extents: job.ndt_extents,
      description: job.description,
    },
  })
  if (insertError) {
    await supabase.storage.from('reports').remove([filePath])
    throw insertError
  }
  return report
}
